import { v4 as uuid } from '@lukeed/uuid'
import {
  Alert,
  App as AntdApp,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useCallback, useEffect, useState } from 'react'
import $api from '../../API'
import { authTokenCache } from '../../lib/auth-token-cache'
import {
  useSocketConnection,
  useSocketEvent,
  type ConnectedClient,
  type EventLogEntry
} from '../../lib/socket'

const { Title, Text } = Typography
const { TextArea } = Input

const EVENT_TYPES = [
  { value: 'user:message', label: 'user:message (custom)' },
  { value: 'debug:echo', label: 'debug:echo' },
  { value: 'debug:broadcast', label: 'debug:broadcast' },
  { value: 'check-run:status-changed', label: 'check-run:status-changed' },
  { value: 'payment:processed', label: 'payment:processed' },
  { value: 'notification:new', label: 'notification:new' }
]

const DEFAULT_PAYLOADS: Record<string, object> = {
  'user:message': { from: '', message: '', timestamp: new Date().toISOString() },
  'debug:echo': { message: 'Hello from debug!', timestamp: new Date().toISOString() },
  'debug:broadcast': { from: 'admin', message: 'Test broadcast message' },
  'check-run:status-changed': { checkRunId: 123, status: 'completed' },
  'payment:processed': { paymentId: 456, amount: 1000 },
  'notification:new': { userId: 'user123', title: 'Test', message: 'Test notification' }
}

export function SocketDebugPage() {
  const { notification } = AntdApp.useApp()
  const { isConnected, connectionError, socketId } = useSocketConnection()
  const [eventLog, setEventLog] = useState<EventLogEntry[]>([])
  const [connectedClients, setConnectedClients] = useState<ConnectedClient[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [emitting, setEmitting] = useState(false)
  const [selectedEventType, setSelectedEventType] = useState('user:message')
  const [form] = Form.useForm()

  // Subscribe to all events for logging
  useSocketEvent('debug:echo', payload => {
    addToLog('in', 'debug:echo', payload)
  })

  useSocketEvent('debug:broadcast', payload => {
    addToLog('in', 'debug:broadcast', payload)
  })

  useSocketEvent('user:message', payload => {
    addToLog('in', 'user:message', payload)
    notification.info({
      message: `Message from ${payload.from}`,
      description: payload.message
    })
  })

  useSocketEvent('check-run:status-changed', payload => {
    addToLog('in', 'check-run:status-changed', payload)
  })

  useSocketEvent('payment:processed', payload => {
    addToLog('in', 'payment:processed', payload)
  })

  useSocketEvent('notification:new', payload => {
    addToLog('in', 'notification:new', payload)
    notification.info({
      message: payload.title,
      description: payload.message
    })
  })

  const addToLog = useCallback((direction: 'in' | 'out', event: string, payload: unknown) => {
    setEventLog(prev => [
      {
        id: uuid(),
        direction,
        event,
        payload,
        timestamp: new Date().toISOString()
      },
      ...prev.slice(0, 99) // Keep last 100 entries
    ])
  }, [])

  const fetchConnectedClients = useCallback(async () => {
    setLoadingClients(true)
    try {
      const eventsUrl = window.env?.EVENTS_URL
      if (!eventsUrl) {
        console.warn('EVENTS_URL not configured')
        return
      }

      // Get auth token for the events service
      const token = await authTokenCache.getToken()
      if (!token) {
        console.error('No auth token available for events API')
        return
      }

      // Construct API URL using base path (extract from socket.io path)
      // EVENTS_PATH is /events/socket.io, so we extract /events as base path
      const eventsPath = window.env?.EVENTS_PATH || '/events/socket.io'
      const basePath = eventsPath.replace('/socket.io', '')
      const apiUrl = `${eventsUrl}${basePath}/api/clients`

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = (await response.json()) as { clients?: ConnectedClient[] }
      setConnectedClients(data.clients || [])
    } catch (error) {
      console.error('Failed to fetch connected clients:', error)
    } finally {
      setLoadingClients(false)
    }
  }, [])

  const handleEmit = async (values: { type: string; payload: string; message?: string }) => {
    setEmitting(true)
    try {
      let payload: unknown

      if (values.type === 'user:message') {
        // For user:message, construct payload from the message field
        const token = await authTokenCache.getToken()
        // Extract email from JWT token (base64 decode the payload part)
        let userEmail = 'Anonymous'
        if (token) {
          try {
            const tokenParts = token.split('.')
            if (tokenParts[1]) {
              const tokenPayload = JSON.parse(atob(tokenParts[1])) as {
                email?: string
                sub?: string
              }
              userEmail = tokenPayload.email || tokenPayload.sub || 'Anonymous'
            }
          } catch {
            // Ignore token parsing errors
          }
        }
        payload = {
          from: userEmail,
          message: values.message || '',
          timestamp: new Date().toISOString()
        }
      } else {
        payload = JSON.parse(values.payload)
      }

      await $api.post('/diagnostic/events/emit', {
        type: values.type,
        payload
      })
      addToLog('out', values.type, payload)
    } catch (error) {
      console.error('Failed to emit event:', error)
    } finally {
      setEmitting(false)
    }
  }

  const handleEventTypeChange = (type: string) => {
    setSelectedEventType(type)
    if (type !== 'user:message') {
      const defaultPayload = DEFAULT_PAYLOADS[type]
      if (defaultPayload) {
        form.setFieldValue('payload', JSON.stringify(defaultPayload, null, 2))
      }
    }
  }

  // Fetch clients on mount and when connected
  useEffect(() => {
    if (isConnected) {
      fetchConnectedClients()
    }
  }, [isConnected, fetchConnectedClients])

  const eventLogColumns: ColumnsType<EventLogEntry> = [
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      width: 80,
      render: dir => (
        <Tag color={dir === 'in' ? 'green' : 'blue'}>{dir === 'in' ? 'IN' : 'OUT'}</Tag>
      )
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      width: 200,
      render: event => <Text code>{event}</Text>
    },
    {
      title: 'Payload',
      dataIndex: 'payload',
      key: 'payload',
      render: payload => (
        <Text code style={{ fontSize: 12 }}>
          {JSON.stringify(payload)}
        </Text>
      )
    },
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 100,
      render: ts => new Date(ts).toLocaleTimeString()
    }
  ]

  const clientsColumns: ColumnsType<ConnectedClient> = [
    {
      title: 'Socket ID',
      dataIndex: 'socketId',
      key: 'socketId',
      render: id => <Text code>{id}</Text>
    },
    {
      title: 'User',
      dataIndex: 'email',
      key: 'email',
      render: (email, record) => email || record.userId
    },
    {
      title: 'Connected At',
      dataIndex: 'connectedAt',
      key: 'connectedAt',
      render: ts => new Date(ts).toLocaleString()
    }
  ]

  return (
    <div className='p-6'>
      <Title level={2}>Socket.io Debug Console</Title>

      <Row gutter={[16, 16]}>
        {/* Connection Status */}
        <Col span={24}>
          <Card size='small'>
            <Space size='large'>
              <Space>
                <Text strong>Status:</Text>
                <Badge
                  status={isConnected ? 'success' : 'error'}
                  text={isConnected ? 'Connected' : 'Disconnected'}
                />
              </Space>
              {socketId && (
                <Space>
                  <Text strong>Socket ID:</Text>
                  <Text code>{socketId}</Text>
                </Space>
              )}
            </Space>
            {connectionError && (
              <Alert type='error' message={connectionError} className='mt-2' showIcon />
            )}
          </Card>
        </Col>

        {/* Connected Clients */}
        <Col span={12}>
          <Card
            title='Connected Clients'
            extra={
              <Button size='small' onClick={fetchConnectedClients} loading={loadingClients}>
                Refresh
              </Button>
            }
          >
            <Table
              dataSource={connectedClients}
              columns={clientsColumns}
              rowKey='socketId'
              size='small'
              pagination={false}
              scroll={{ y: 200 }}
            />
          </Card>
        </Col>

        {/* Emit Event Form */}
        <Col span={12}>
          <Card title='Send Test Event'>
            <Form
              form={form}
              layout='vertical'
              onFinish={handleEmit}
              initialValues={{
                type: 'user:message',
                message: '',
                payload: JSON.stringify(DEFAULT_PAYLOADS['debug:echo'], null, 2)
              }}
            >
              <Form.Item name='type' label='Event Type' rules={[{ required: true }]}>
                <Select options={EVENT_TYPES} onChange={handleEventTypeChange} />
              </Form.Item>
              {selectedEventType === 'user:message' ? (
                <Form.Item
                  name='message'
                  label='Your Message'
                  rules={[{ required: true, message: 'Please enter a message' }]}
                >
                  <TextArea rows={4} placeholder='Type your message here...' />
                </Form.Item>
              ) : (
                <Form.Item
                  name='payload'
                  label='Payload (JSON)'
                  rules={[
                    { required: true },
                    {
                      validator: (_, value) => {
                        try {
                          JSON.parse(value)
                          return Promise.resolve()
                        } catch {
                          return Promise.reject('Invalid JSON')
                        }
                      }
                    }
                  ]}
                >
                  <TextArea rows={4} style={{ fontFamily: 'monospace' }} />
                </Form.Item>
              )}
              <Form.Item>
                <Button type='primary' htmlType='submit' loading={emitting}>
                  Send Event
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Event Log */}
        <Col span={24}>
          <Card
            title='Event Log'
            extra={
              <Button size='small' onClick={() => setEventLog([])}>
                Clear
              </Button>
            }
          >
            <Table
              dataSource={eventLog}
              columns={eventLogColumns}
              rowKey='id'
              size='small'
              pagination={false}
              scroll={{ y: 300 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}
